import type { SingleValue } from '@tempo-io/sui-combobox';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { Select } from '@tempo-io/sui-select';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCustomFieldSearchQuery } from '@/lib/queries/common/useGetCustomFieldsQuery';
import { headerToPropertySelector } from '@/lib/utils/roadmapping/commonLayoutUtils';
import { SelectToolTip } from '../common/SelectToolTip';
import { DateHeaderSubMenu } from './DateHeaderSubMenu';
import { useHeaderLabel } from './useHeaderMenuLabel';
import { type DisabledField, getSelectableFields, type ISelectOption, isHeaderDate } from './utils';

export type FieldSelectionProps = {
  value?: string;
  disabledFields?: Array<DisabledField>;
  includeDateHeader?: boolean;
  allowCustomFields?: boolean;
  isClearable?: boolean;
  onValueSelected: (value: string | null) => void;
};

export const isFieldSelectionDisabled = (
  option: ISelectOption,
  disabledFields: Array<DisabledField>,
) => {
  return disabledFields.some((disabledField) => disabledField.field === option.value);
};

export function HeaderFieldSelection({
  inputId,
  disabledFields = [],
  includeDateHeader = true,
  allowCustomFields = true,
  isClearable = false,
  value,
  onValueSelected,
}: FieldSelectionProps & React.ComponentProps<typeof Select>) {
  const { t } = useTranslation();

  const [isDateSubmenuOpen, setIsDateSubmenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const onChange = (selectOption: SingleValue<ISelectOption>) => {
    if (!selectOption) {
      onValueSelected(null);
      return;
    }
    if (selectOption.value === 'itemDate') {
      setIsDateSubmenuOpen(true);
      return;
    }
    onValueSelected(selectOption.value);
  };

  const { data, isLoading } = useGetCustomFieldSearchQuery({
    search: inputValue,
  });

  const label = useHeaderLabel(headerToPropertySelector(value));
  const isDateHeader = isHeaderDate(value);

  const selectedOption: ISelectOption | null = value
    ? {
        value: isDateHeader ? 'itemDate' : value,
        label,
      }
    : null;

  const selectableFields = useMemo(
    () =>
      getSelectableFields({
        fields: data || [],
        t,
        includeItemDate: includeDateHeader,
        allowCustomFields,
      }),
    [data, t, includeDateHeader, allowCustomFields],
  );

  return (
    <div className="relative">
      <Select
        value={selectedOption}
        onChange={(option) => onChange(option)}
        inputValue={inputValue}
        onInputChange={(e) => setInputValue(e)}
        isSearchable
        isLoading={isLoading}
        isOptionDisabled={(option) =>
          isFieldSelectionDisabled(option as ISelectOption, disabledFields)
        }
        maxMenuHeight={200}
        formatOptionLabel={(option) => {
          const disabledField = disabledFields.find((field) => field.field === option.value);
          if (disabledField) {
            return <SelectToolTip label={option.label} toolTip={disabledField.reason} />;
          }

          return option.label;
        }}
        inputId={inputId}
        options={selectableFields}
        isClearable={isClearable}
      />
      <Popover open={isDateSubmenuOpen} onOpenChange={setIsDateSubmenuOpen} modal={false}>
        <PopoverTrigger asChild>
          <span style={{ position: 'absolute', left: 0, top: '100%', width: 1, height: 1 }} />
        </PopoverTrigger>
        <PopoverContent
          className="w-[350px] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DateHeaderSubMenu
            onValueSelected={onValueSelected}
            onClose={() => setIsDateSubmenuOpen(false)}
            value={isDateHeader ? value : undefined}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
